import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, IconButton,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, Badge, InputAdornment, Tooltip, Grid, Chip,
  Drawer, List, ListItem, ListItemText, ListItemIcon, Collapse,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Add, Remove, Delete, Search, QrCodeScanner, ShoppingCart,
  PersonSearch, LocalOffer, Print, ReceiptLong,
  Inbox, CheckCircle, Close, PauseCircle,
  CreditCard, PhoneAndroid, AttachMoney, Store, ArrowForward,
  MonitorHeart, PointOfSale, Undo, Block, ExpandMore, ExpandLess,
} from '@mui/icons-material';
import { io as socketIO } from 'socket.io-client';
import {
  fetchProducts, fetchCategories, createPOSSale, getCurrentShift,
  openShift, closeShift, getPOSQueue, searchCustomers,
  validateDiscount, lookupBarcode, holdTransaction as apiHoldTransaction,
  getHeldTransactions, recallHeld, updateHeld,
} from '../../api/api';

const SOCKET_URL = 'https://backend-mtaani.onrender.com';

/* ─── HELPERS ─── */
const KES = (n) => `KES ${Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`;
const VAT_RATE = 0.16;
const vatOf = (amt) => amt * VAT_RATE / (1 + VAT_RATE);
/* Highlight matching substring in product names */
function HighlightText({ text = '', query = '' }) {
  if (!query.trim()) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <Box component="span" sx={{ fontWeight: 900, color: 'primary.main' }}>
        {text.slice(idx, idx + query.length)}
      </Box>
      {text.slice(idx + query.length)}
    </>
  );
}

export default function POSTerminal() {
  const theme = useTheme();
  const { palette } = theme;

  /* ─── Theme shorthand ─── */
  const P = {
    blue:   palette.primary.main,
    green:  palette.success.main,
    orange: palette.warning.main,
    red:    palette.error.main,
    purple: palette.secondary.main,
    border: palette.divider,
  };

  /* ── Catalog ── */
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(true);

  /* ── Scanner ── */
  const [scanInput, setScanInput] = useState('');
  const [qtyMultiplier, setQtyMultiplier] = useState(1);
  const [lookupError, setLookupError] = useState('');
  const scanRef = useRef(null);
  const searchRef = useRef(null);
  const cartEndRef = useRef(null);

  /* ── Cart ── */
  const [cart, setCart] = useState([]);
  const [selectedLine, setSelectedLine] = useState(null);
  const [heldTxns, setHeldTxns] = useState([]);

  /* ── Customer ── */
  const [customerPhone, setCustomerPhone] = useState('');
  const [customer, setCustomer] = useState(null);
  const [searchingCustomer, setSearchingCustomer] = useState(false);

  /* ── Discount ── */
  const [discountCode, setDiscountCode] = useState('');
  const [discount, setDiscount] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [showDiscount, setShowDiscount] = useState(false);

  /* ── Payment ── */
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountReceived, setAmountReceived] = useState('');
  const [mpesaRef, setMpesaRef] = useState('');

  /* ── Shift ── */
  const [currentShift, setCurrentShift] = useState(null);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [openFloat, setOpenFloat] = useState('');
  const [closeFloat, setCloseFloat] = useState('');
  const [shiftProcessing, setShiftProcessing] = useState(false);

  /* ── Sale state ── */
  const [processing, setProcessing] = useState(false);
  const [saleError, setSaleError] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const [time, setTime] = useState(new Date());

  /* ── Live sync ── */
  const socketRef = useRef(null);
  const terminalId = useRef('T-' + Math.random().toString(36).slice(2, 7).toUpperCase());
  const [onlineTerminals, setOnlineTerminals] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [showFeed, setShowFeed] = useState(false);
  const [terminalCarts, setTerminalCarts] = useState({});
  const [editingHoldId, setEditingHoldId] = useState(null);
  const [takeoverNotice, setTakeoverNotice] = useState(null);
  const cartBroadcastTimer = useRef(null);
  const holdSyncTimer = useRef(null);

  /* ── Init ── */
  useEffect(() => {
    loadProducts(); loadCategories(); loadShift(); loadQueue(); loadHeldTransactions();
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  /* ── Socket connection ── */
  useEffect(() => {
    const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
    const userId = user._id || user.id || localStorage.getItem('userId');
    const cashierName = user.name || 'Cashier';

    const socket = socketIO(SOCKET_URL, { transports: ['websocket'], reconnection: true });
    socketRef.current = socket;

    socket.on('connect', () => {
      if (userId) socket.emit('auth', userId);
      socket.emit('pos:join', { userId, terminalId: terminalId.current, cashierName });
    });

    const pushActivity = (entry) =>
      setActivityFeed(prev => [entry, ...prev].slice(0, 40));

    socket.on('pos:sale_completed', (data) => {
      if (data.terminalId === terminalId.current) return;
      pushActivity({ type: 'sale', ...data });
      setProducts(prev => prev.map(p => {
        const hit = data.items?.find(i => i.productId?.toString() === p._id?.toString());
        if (!hit) return p;
        return { ...p, stock: Math.max(0, (p.stock ?? 0) - hit.quantity) };
      }));
    });

    socket.on('pos:sale_voided', (data) => {
      pushActivity({ type: 'void', ...data });
    });

    socket.on('pos:sale_refunded', (data) => {
      pushActivity({ type: 'refund', ...data });
    });

    socket.on('pos:transaction_held', (data) => {
      if (data.terminalId === terminalId.current) return;
      pushActivity({ type: 'hold', ...data });
      setHeldTxns(prev => {
        if (prev.find(h => h._id === data.holdId)) return prev;
        return [...prev, {
          _id: data.holdId,
          saleNumber: data.saleNumber,
          cart: (data.items || []).map(i => ({
            product: { _id: i.productId, title: i.title, price: i.unitPrice },
            quantity: i.quantity,
          })),
          customer: null,
          discount: null,
          discountCode: data.discountCode || '',
          customerPhone: '',
          holdNote: data.holdNote || '',
          cashier: data.cashier,
          terminalId: data.terminalId,
          fromRemote: true,
          heldAt: new Date(data.at).getTime(),
        }];
      });
    });

    socket.on('pos:transaction_recalled', (data) => {
      setHeldTxns(prev => prev.filter(h => h._id !== data.holdId));
    });

    socket.on('pos:shift_opened', (data) => {
      pushActivity({ type: 'shift_open', ...data });
    });

    socket.on('pos:shift_closed', (data) => {
      pushActivity({ type: 'shift_close', ...data });
    });

    socket.on('pos:terminal_online', (data) => {
      setOnlineTerminals(prev => {
        if (prev.find(t => t.terminalId === data.terminalId)) return prev;
        return [...prev, data];
      });
    });

    socket.on('pos:terminal_offline', (data) => {
      setOnlineTerminals(prev => prev.filter(t => t.terminalId !== data.terminalId));
      setTerminalCarts(prev => { const n = { ...prev }; delete n[data.terminalId]; return n; });
    });

    // Live cart updates from other terminals
    socket.on('pos:cart_updated', (data) => {
      if (data.terminalId === terminalId.current) return;
      setTerminalCarts(prev => ({ ...prev, [data.terminalId]: data }));
    });

    // Hold item edits from other terminals
    socket.on('pos:hold_updated', (data) => {
      setHeldTxns(prev => prev.map(h => {
        if (h._id?.toString() !== data.holdId?.toString()) return h;
        return {
          ...h,
          cart: (data.items || []).map(i => ({
            product: { _id: i.productId, title: i.title, price: i.unitPrice },
            quantity: i.quantity,
          })),
        };
      }));
    });

    // Another terminal took over MY cart — clear it and show notice
    socket.on('pos:cart_takeover', (data) => {
      if (data.fromTerminalId !== terminalId.current) return;
      setCart([]);
      setCustomer(null); setDiscount(null); setDiscountCode('');
      setCustomerPhone(''); setEditingHoldId(null);
      setTakeoverNotice(`Cart taken over by ${data.takenBy || data.toTerminalId}`);
      setTimeout(() => setTakeoverNotice(null), 6000);
    });

    // Another terminal pushed an item into MY cart
    socket.on('pos:push_item', (data) => {
      if (data.targetTerminalId !== terminalId.current) return;
      const item = data.item;
      if (!item) return;
      setCart(prev => {
        const ex = prev.find(i => i.product._id?.toString() === item.productId?.toString());
        if (ex) return prev.map(i =>
          i.product._id?.toString() === item.productId?.toString()
            ? { ...i, quantity: i.quantity + (item.quantity || 1) }
            : i
        );
        return [...prev, {
          product: { _id: item.productId, title: item.title, price: item.unitPrice },
          quantity: item.quantity || 1,
        }];
      });
    });

    return () => {
      socket.emit('pos:leave', { terminalId: terminalId.current });
      socket.disconnect();
    };
  }, []);

  useEffect(() => { cartEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [cart.length]);

  /* ── Broadcast active cart to all other terminals (debounced 300 ms) ── */
  useEffect(() => {
    if (!socketRef.current) return;
    clearTimeout(cartBroadcastTimer.current);
    cartBroadcastTimer.current = setTimeout(() => {
      const cartTotal = cart.reduce((s, i) => s + (i.product.price || 0) * i.quantity, 0);
      socketRef.current.emit('pos:cart_updated', {
        terminalId: terminalId.current,
        items: cart.map(i => ({
          productId: i.product._id,
          title: i.product.title,
          quantity: i.quantity,
          unitPrice: i.product.price,
          lineTotal: (i.product.price || 0) * i.quantity,
        })),
        itemCount: cart.length,
        total: cartTotal,
        editingHoldId: editingHoldId || null,
        at: new Date().toISOString(),
      });
    }, 300);
  }, [cart, editingHoldId]);

  /* ── Sync held transaction edits back to DB + broadcast (debounced 600 ms) ── */
  useEffect(() => {
    if (!editingHoldId || !cart.length) return;
    clearTimeout(holdSyncTimer.current);
    holdSyncTimer.current = setTimeout(async () => {
      const cartSubtotal = cart.reduce((s, i) => s + (i.product.price || 0) * i.quantity, 0);
      try {
        await updateHeld(editingHoldId, {
          items: cart.map(i => ({
            productId: i.product._id,
            title: i.product.title,
            quantity: i.quantity,
            unitPrice: i.product.price,
          })),
          subtotal: cartSubtotal,
          total: cartSubtotal,
          terminalId: terminalId.current,
        });
      } catch { /* hold may have been processed — ignore */ }
    }, 600);
  }, [cart, editingHoldId]);

  /* Press `/` anywhere (not in an input) to jump to product search */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const refocusScanner = useCallback(() => { setTimeout(() => scanRef.current?.focus(), 60); }, []);

  /* ── Data loaders ── */
  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      const res = await fetchProducts();
      const raw = res.data;
      setProducts(Array.isArray(raw) ? raw : Array.isArray(raw?.products) ? raw.products : Array.isArray(raw?.data) ? raw.data : []);
    } catch { setProducts([]); } finally { setLoadingProducts(false); }
  };

  const loadCategories = async () => {
    try {
      const res = await fetchCategories();
      const raw = res.data;
      setCategories(Array.isArray(raw) ? raw : Array.isArray(raw?.categories) ? raw.categories : Array.isArray(raw?.data) ? raw.data : []);
    } catch { setCategories([]); }
  };

  const loadShift = async () => {
    try { const res = await getCurrentShift(); setCurrentShift(res.data?.shift || res.data || null); }
    catch { setCurrentShift(null); }
  };

  const loadQueue = async () => {
    try { const res = await getPOSQueue(); const d = res.data; setQueueCount(d?.count ?? (Array.isArray(d) ? d.length : 0)); }
    catch { setQueueCount(0); }
  };

  const loadHeldTransactions = async () => {
    try {
      const res = await getHeldTransactions();
      const held = res.data?.held || [];
      setHeldTxns(held.map(h => ({
        _id: h._id,
        saleNumber: h.saleNumber,
        cart: (h.items || []).map(i => ({
          product: { _id: i.product?._id || i.product, title: i.title, price: i.unitPrice },
          quantity: i.quantity,
        })),
        customer: h.customer || null,
        discount: null,
        discountCode: '',
        customerPhone: h.customer?.phone || '',
        holdNote: h.holdNote || '',
        cashier: h.cashier,
        terminalId: h.terminalId,
        fromRemote: true,
        heldAt: new Date(h.createdAt).getTime(),
      })));
    } catch { /* keep local state if API fails */ }
  };

  /* ── Filtered products ── */
  const filteredProducts = useMemo(() => {
    let list = products.filter(p => p.isActive !== false && !p.deleted);
    if (!searchQuery.trim()) return list;
    const q = searchQuery.trim().toLowerCase();
    list = list.filter(p =>
      p.title?.toLowerCase().includes(q) ||
      p.barcode?.includes(q) ||
      p.sku?.toLowerCase().includes(q)
    );
    list.sort((a, b) => {
      const aT = (a.title || '').toLowerCase();
      const bT = (b.title || '').toLowerCase();
      return (aT === q ? 0 : aT.startsWith(q) ? 1 : 2) - (bT === q ? 0 : bT.startsWith(q) ? 1 : 2);
    });
    return list;
  }, [products, searchQuery]);


  /* ── Scanner handler ── */
  const handleScanKeyDown = async (e) => {
    if (e.key !== 'Enter') return;
    const raw = scanInput.trim();
    if (!raw) return;
    const multOnly = raw.match(/^(\d+)[*x]$/i);
    if (multOnly) { setQtyMultiplier(parseInt(multOnly[1])); setScanInput(''); return; }
    const withCode = raw.match(/^(\d+)[*x](.+)$/i);
    const qty = withCode ? parseInt(withCode[1]) : qtyMultiplier;
    const code = withCode ? withCode[2].trim() : raw;
    setLookupError('');
    let found = null;
    try { const res = await lookupBarcode(code); found = res.data?.product || (res.data?._id ? res.data : null); }
    catch { /* fall through */ }
    if (!found) found = products.find(p => p.barcode === code || p.sku?.toLowerCase() === code.toLowerCase());
    if (found) addToCart(found, qty);
    else setLookupError(`"${code}" not found`);
    setScanInput('');
    if (qty > 1 || withCode) setQtyMultiplier(1);
  };

  /* ── Cart helpers ── */
  const addToCart = useCallback((product, qty = 1) => {
    setCart(prev => {
      const ex = prev.find(i => i.product._id === product._id);
      if (ex) return prev.map(i => i.product._id === product._id ? { ...i, quantity: i.quantity + qty } : i);
      return [...prev, { product, quantity: qty }];
    });
    setSelectedLine(product._id);
    setLookupError('');
  }, []);

  const handleProductClick = useCallback((p) => {
    addToCart(p, qtyMultiplier);
    if (qtyMultiplier > 1) setQtyMultiplier(1);
    refocusScanner();
  }, [addToCart, qtyMultiplier, refocusScanner]);

  /* Enter on product search → add top result, clear query */
  const handleProductSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredProducts.length > 0) {
        handleProductClick(filteredProducts[0]);
        setSearchQuery('');
      }
    } else if (e.key === 'Escape') {
      setSearchQuery('');
      scanRef.current?.focus();
    }
  }, [filteredProducts, handleProductClick]);

  const updateQty = (id, delta) => {
    setCart(prev => prev.map(i => i.product._id === id ? { ...i, quantity: i.quantity + delta } : i).filter(i => i.quantity > 0));
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(i => i.product._id !== id));
    if (selectedLine === id) setSelectedLine(null);
    refocusScanner();
  };

  /* ── Hold / Recall ── */
  const holdTransaction = async () => {
    if (!cart.length) return;
    const payload = {
      items: cart.map(i => ({ productId: i.product._id, title: i.product.title, quantity: i.quantity, unitPrice: i.product.price })),
      subtotal,
      total,
      discountAmount: discountAmt,
      customerId: customer?._id || customer?.id,
      discountCode: discount ? discountCode : '',
      terminalId: terminalId.current,
    };
    try {
      const res = await apiHoldTransaction(payload);
      const held = res.data?.held;
      setHeldTxns(p => [...p, {
        _id: held._id,
        saleNumber: held.saleNumber,
        cart, customer, discount, discountCode, customerPhone,
        holdNote: '', terminalId: terminalId.current, fromRemote: false, heldAt: Date.now(),
      }]);
      resetSale(false);
    } catch {
      setHeldTxns(p => [...p, { cart, customer, discount, discountCode, customerPhone, holdNote: '', heldAt: Date.now() }]);
      resetSale(false);
    }
  };

  const recallTransaction = async (idx) => {
    const h = heldTxns[idx];
    const holdId = h._id || null;
    if (holdId) {
      try { await recallHeld(holdId); } catch { /* ignore */ }
    }
    setCart(h.cart); setCustomer(h.customer || null); setDiscount(h.discount || null);
    setDiscountCode(h.discountCode || ''); setCustomerPhone(h.customerPhone || '');
    setHeldTxns(p => p.filter((_, i) => i !== idx));
    setEditingHoldId(holdId);
    setAmountReceived(''); setSaleError(''); setSelectedLine(null);
    refocusScanner();
  };

  const processNext = async () => {
    if (heldTxns.length === 0) return;
    const [nextHeld, ...remaining] = heldTxns;
    if (cart.length > 0) {
      const payload = {
        items: cart.map(i => ({ productId: i.product._id, title: i.product.title, quantity: i.quantity, unitPrice: i.product.price })),
        subtotal, total, discountAmount: discountAmt,
        customerId: customer?._id || customer?.id,
        discountCode: discount ? discountCode : '',
        terminalId: terminalId.current,
      };
      try {
        const res = await apiHoldTransaction(payload);
        const held = res.data?.held;
        setHeldTxns([...remaining, {
          _id: held._id, saleNumber: held.saleNumber,
          cart, customer, discount, discountCode, customerPhone,
          holdNote: '', terminalId: terminalId.current, fromRemote: false, heldAt: Date.now(),
        }]);
      } catch {
        setHeldTxns([...remaining, { cart, customer, discount, discountCode, customerPhone, heldAt: Date.now() }]);
      }
    } else {
      setHeldTxns(remaining);
    }
    if (nextHeld._id) {
      try { await recallHeld(nextHeld._id); } catch { /* ignore */ }
    }
    setCart(nextHeld.cart); setCustomer(nextHeld.customer || null);
    setDiscount(nextHeld.discount || null); setDiscountCode(nextHeld.discountCode || '');
    setCustomerPhone(nextHeld.customerPhone || '');
    setAmountReceived(''); setSaleError(''); setSelectedLine(null);
    refocusScanner();
  };

  /* ── Numpad ── */
  const handleNumpad = (val) => {
    if (val === 'BACK') { setAmountReceived(p => p.slice(0, -1)); }
    else if (val === 'CLR') { setAmountReceived(''); }
    else if (val === 'EXACT') { setAmountReceived(total.toFixed(2)); }
    else if (typeof val === 'number') { setAmountReceived(String(val)); }
    else {
      setAmountReceived(p => {
        if (val === '.' && p.includes('.')) return p;
        const next = p + val;
        if (next === '00') return '0';
        const parts = next.split('.');
        if (parts[1] && parts[1].length > 2) return p;
        return next;
      });
    }
  };

  /* ── Totals ── */
  const subtotal = cart.reduce((s, i) => s + (i.product.price || 0) * i.quantity, 0);
  const discountAmt = discount
    ? discount.type === 'percentage' ? (subtotal * discount.value) / 100
    : discount.type === 'fixed' ? Math.min(discount.value, subtotal) : 0
    : 0;
  const total = Math.max(0, subtotal - discountAmt);
  const vat = vatOf(total);
  const change = paymentMethod === 'cash' ? Math.max(0, Number(amountReceived || 0) - total) : 0;
  const cashReady = paymentMethod === 'cash' && Number(amountReceived || 0) >= total && total > 0;

  /* ── Customer ── */
  const handleFindCustomer = async () => {
    if (!customerPhone.trim()) return;
    setSearchingCustomer(true);
    try {
      const res = await searchCustomers(customerPhone.trim());
      const list = res.data?.customers || res.data || [];
      setCustomer(list[0] || null);
      if (!list[0]) setLookupError('Customer not found.');
    } catch { setCustomer(null); }
    finally { setSearchingCustomer(false); refocusScanner(); }
  };

  /* ── Discount ── */
  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountError(''); setValidatingDiscount(true);
    try {
      const res = await validateDiscount({ code: discountCode.trim().toUpperCase(), cartTotal: subtotal, items: cart.map(i => ({ productId: i.product._id, quantity: i.quantity })) });
      setDiscount(res.data?.discount || res.data);
    } catch (err) {
      setDiscountError(err.response?.data?.message || 'Invalid code.'); setDiscount(null);
    } finally { setValidatingDiscount(false); refocusScanner(); }
  };

  /* ── Shift ── */
  const handleOpenShift = async () => {
    setShiftProcessing(true);
    try {
      const res = await openShift({ openingFloat: Number(openFloat || 0) });
      setCurrentShift(res.data?.shift || res.data); setShiftOpen(false); setOpenFloat('');
    } catch (err) { setSaleError(err.response?.data?.message || 'Failed to open shift.'); }
    finally { setShiftProcessing(false); }
  };

  const handleCloseShift = async () => {
    if (!currentShift) return;
    setShiftProcessing(true);
    try {
      await closeShift(currentShift._id || currentShift.id, { closingFloat: Number(closeFloat || 0) });
      setCurrentShift(null); setShiftOpen(false); setCloseFloat('');
    } catch (err) { setSaleError(err.response?.data?.message || 'Failed to close shift.'); }
    finally { setShiftProcessing(false); }
  };

  /* ── Process sale ── */
  const handleProcessSale = async () => {
    setSaleError('');
    if (!cart.length) { setSaleError('Cart is empty.'); return; }
    if (paymentMethod === 'cash' && Number(amountReceived || 0) < total) {
      setSaleError('Amount received is less than total.'); return;
    }
    const payload = {
      shiftId: currentShift ? (currentShift._id || currentShift.id) : undefined,
      customerId: customer?._id || customer?.id,
      items: cart.map(i => ({ productId: i.product._id, quantity: i.quantity, unitPrice: i.product.price })),
      discountCode: discount ? discountCode.trim().toUpperCase() : undefined,
      subtotal, discountAmount: discountAmt, total, paymentMethod,
      payments: [{ method: paymentMethod, amount: paymentMethod === 'cash' ? Number(amountReceived) : total }],
      amountReceived: paymentMethod === 'cash' ? Number(amountReceived) : total,
      change, mpesaRef: paymentMethod === 'mpesa' ? mpesaRef : undefined,
      terminalId: terminalId.current,
    };
    setProcessing(true);
    try {
      const res = await createPOSSale(payload);
      const sale = res.data?.sale || res.data;
      setReceipt({ ...sale, items: cart, total, subtotal, discountAmt, vat, change, paymentMethod, amountReceived, customer });
      setReceiptOpen(true);
    } catch (err) { setSaleError(err.response?.data?.message || 'Failed to process sale.'); }
    finally { setProcessing(false); }
  };

  /* ── Reset ── */
  const resetSale = (full = true) => {
    setCart([]); setCustomer(null); setCustomerPhone(''); setDiscount(null);
    setDiscountCode(''); setDiscountError(''); setAmountReceived(''); setMpesaRef('');
    setPaymentMethod('cash'); setSaleError(''); setLookupError('');
    setSelectedLine(null); setQtyMultiplier(1); setShowDiscount(false);
    setEditingHoldId(null);
    if (full) { setReceiptOpen(false); setReceipt(null); }
    refocusScanner();
  };

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', overflow: 'hidden' }}>

      {/* ══ HEADER ══ */}
      <Box sx={{
        height: 50, flexShrink: 0, bgcolor: 'background.paper',
        borderBottom: '1px solid', borderColor: 'divider',
        display: 'flex', alignItems: 'center', px: 2, gap: 2,
      }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
          <Store sx={{ color: 'primary.main', fontSize: 18 }} />
          <Typography fontWeight={800} fontSize={14} letterSpacing={1.5} sx={{ textTransform: 'uppercase' }}>
            Mtaani POS
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Hold slots */}
        <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center' }}>
          <Typography fontSize={10} color="text.disabled" fontWeight={600} mr={0.5}>HOLDS:</Typography>
          {Array.from({ length: 5 }).map((_, i) => {
            const h = heldTxns[i];
            const ht = h ? h.cart.reduce((s, x) => s + (x.product.price || 0) * x.quantity, 0) : 0;
            const beingEdited = h?._id && Object.values(terminalCarts).some(tc => tc.editingHoldId?.toString() === h._id?.toString());
            const isMyEdit = h?._id && editingHoldId === h._id;
            return (
              <Tooltip
                key={i}
                title={h
                  ? beingEdited ? `Being edited by another terminal` : `Recall – ${h.cart.length} item(s) · ${KES(ht)}`
                  : 'Empty'}
                arrow
              >
                <Box
                  onClick={() => h && !beingEdited && recallTransaction(i)}
                  sx={{
                    height: 28, minWidth: 52, borderRadius: 1, px: 1,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    cursor: h && !beingEdited ? 'pointer' : 'default',
                    bgcolor: isMyEdit ? alpha(P.blue, 0.18) : beingEdited ? alpha(P.red, 0.1) : h ? alpha(P.orange, 0.15) : 'action.hover',
                    border: `1px solid ${isMyEdit ? alpha(P.blue, 0.5) : beingEdited ? alpha(P.red, 0.4) : h ? alpha(P.orange, 0.4) : P.border}`,
                    transition: 'all 0.12s',
                    '&:hover': h && !beingEdited ? { bgcolor: alpha(P.orange, 0.25), transform: 'translateY(-1px)' } : {},
                  }}
                >
                  <Typography fontSize={8.5} fontWeight={700} lineHeight={1.2}
                    color={isMyEdit ? 'primary.main' : beingEdited ? 'error.main' : h ? 'warning.main' : 'text.disabled'}>
                    {h ? `H${i + 1} · ${h.cart.length}pc` : `H${i + 1}`}
                  </Typography>
                  {beingEdited && <Typography fontSize={7} color="error.main" lineHeight={1}>locked</Typography>}
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Online terminals + activity feed toggle */}
        <Tooltip title={`${onlineTerminals.length + 1} terminal(s) online — click to view activity`} arrow>
          <Box
            onClick={() => setShowFeed(v => !v)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.6, px: 1.2, py: 0.5,
              borderRadius: 1, cursor: 'pointer',
              bgcolor: alpha(P.green, 0.08),
              border: `1px solid ${alpha(P.green, 0.3)}`,
              '&:hover': { bgcolor: alpha(P.green, 0.15) },
            }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'success.main', animation: 'pulse 2s infinite',
              '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
            <Typography fontSize={11} fontWeight={700} color="success.main">
              {onlineTerminals.length + 1} LIVE
            </Typography>
            <MonitorHeart sx={{ fontSize: 13, color: 'success.main' }} />
            {activityFeed.length > 0 && (
              <Box sx={{ bgcolor: 'success.main', color: '#fff', borderRadius: '50%', width: 15, height: 15, fontSize: 8, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {activityFeed.length > 9 ? '9+' : activityFeed.length}
              </Box>
            )}
          </Box>
        </Tooltip>

        {/* Queue badge */}
        <Badge badgeContent={queueCount} color="error" max={99}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.2, py: 0.5, borderRadius: 1, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
            <Inbox sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography fontSize={11} color="text.secondary">Queue</Typography>
          </Box>
        </Badge>

        {/* Shift */}
        <Box
          onClick={() => setShiftOpen(true)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.7, px: 1.2, py: 0.5,
            borderRadius: 1, cursor: 'pointer',
            bgcolor: currentShift ? alpha(P.green, 0.1) : alpha(P.red, 0.1),
            border: `1px solid ${currentShift ? alpha(P.green, 0.35) : alpha(P.red, 0.35)}`,
            '&:hover': { opacity: 0.8 },
          }}
        >
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: currentShift ? 'success.main' : 'error.main' }} />
          <Typography fontSize={11} fontWeight={700} color={currentShift ? 'success.main' : 'error.main'}>
            {currentShift ? 'Shift Open' : 'No Shift'}
          </Typography>
        </Box>

        {/* Terminal ID */}
        <Typography fontSize={10} fontFamily="monospace" color="text.disabled" letterSpacing={0.5}
          title="Terminal ID — unique per browser tab">
          {terminalId.current}
        </Typography>

        {/* Clock */}
        <Typography fontSize={13} fontFamily="monospace" color="text.disabled" letterSpacing={1}>
          {time.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </Typography>
      </Box>

      {/* ══ SCANNER BAR (full width) ══ */}
      <Box sx={{
        px: 2, py: 0.8, bgcolor: 'background.default',
        borderBottom: '1px solid', borderColor: 'divider',
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1,
      }}>
        {qtyMultiplier > 1 && (
          <Chip
            label={`×${qtyMultiplier}`}
            size="small"
            color="warning"
            onDelete={() => setQtyMultiplier(1)}
            sx={{ fontWeight: 900, height: 28, fontSize: 12 }}
          />
        )}
        <TextField
          inputRef={scanRef}
          value={scanInput}
          onChange={e => setScanInput(e.target.value)}
          onKeyDown={handleScanKeyDown}
          placeholder="Scan barcode or type PLU… (3* then scan for qty 3)"
          size="small"
          fullWidth
          autoFocus
          InputProps={{
            startAdornment: <InputAdornment position="start"><QrCodeScanner sx={{ color: 'primary.main', fontSize: 16 }} /></InputAdornment>,
          }}
        />
        {lookupError && (
          <Typography fontSize={11} color="error.main" sx={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 0.3 }}>
            <Close sx={{ fontSize: 12 }} />{lookupError}
          </Typography>
        )}
      </Box>

      {/* ══ TAKEOVER NOTICE ══ */}
      {takeoverNotice && (
        <Alert severity="warning" onClose={() => setTakeoverNotice(null)}
          sx={{ borderRadius: 0, py: 0.4, fontSize: 12, flexShrink: 0 }}>
          {takeoverNotice}
        </Alert>
      )}

      {/* ══ BODY (3 columns) ══ */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── LEFT: Products ── */}
        <Box sx={{
          width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column',
          bgcolor: 'background.paper', borderRight: '1px solid', borderColor: 'divider',
          overflow: 'hidden',
        }}>
          {/* ── Search bar — always visible ── */}
          <Box sx={{ px: 1.2, pt: 1, pb: 0.6, flexShrink: 0 }}>
            <TextField
              inputRef={searchRef}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={handleProductSearchKeyDown}
              placeholder='Search name, barcode, SKU… (/ to focus)'
              size="small"
              fullWidth
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 15, color: searchQuery ? 'primary.main' : 'text.disabled' }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {filteredProducts.length > 0 && (
                        <Box sx={{
                          bgcolor: 'primary.main', color: 'primary.contrastText',
                          borderRadius: 1, px: 0.7, fontSize: 10, fontWeight: 900, lineHeight: 1.8,
                        }}>
                          {filteredProducts.length}
                        </Box>
                      )}
                      <IconButton size="small" onClick={() => { setSearchQuery(''); searchRef.current?.focus(); }} sx={{ p: 0.3 }}>
                        <Close sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </InputAdornment>
                ) : null,
                sx: {
                  borderRadius: 2,
                  fontSize: 13,
                  '& fieldset': searchQuery ? { borderColor: alpha(P.blue, 0.5), borderWidth: 1.5 } : {},
                },
              }}
            />
            {/* Enter hint — only when search has 1+ results */}
            {searchQuery && filteredProducts.length > 0 && (
              <Typography fontSize={10} color="text.disabled" mt={0.4} ml={0.5}>
                ↵ Enter to add <Box component="span" fontWeight={700} color="text.secondary">{filteredProducts[0].title}</Box>
              </Typography>
            )}
            {searchQuery && filteredProducts.length === 0 && !loadingProducts && (
              <Typography fontSize={10} color="error.main" mt={0.4} ml={0.5}>No matches for "{searchQuery}"</Typography>
            )}
          </Box>

          {/* ── Product list / grid ── */}
          <Box sx={{
            flex: 1, overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
          }}>
            {!searchQuery ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1, opacity: 0.3, userSelect: 'none' }}>
                <Search sx={{ fontSize: 38, color: 'text.disabled' }} />
                <Typography fontSize={12} color="text.secondary">Type to search products</Typography>
                <Typography fontSize={10} color="text.disabled">press / to focus</Typography>
              </Box>
            ) : loadingProducts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                <CircularProgress size={26} />
              </Box>
            ) : filteredProducts.length === 0 ? (
              <Box sx={{ textAlign: 'center', pt: 6, opacity: 0.35 }}>
                <ShoppingCart sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
                <Typography fontSize={12} color="text.secondary">No results for "{searchQuery}"</Typography>
              </Box>
            ) : (
              filteredProducts.map((p, idx) => {
                const inCart = cart.find(i => i.product._id === p._id);
                const stock = p.stock ?? p.stockQuantity ?? 99;
                const oos = stock === 0;
                const catName = typeof p.category === 'object'
                  ? (p.category?.name || p.category?.title)
                  : categories.find(c => c._id === p.category)?.name || '';
                return (
                  <Box
                    key={p._id}
                    onClick={() => !oos && handleProductClick(p)}
                    sx={{
                      px: 1.5, py: 0.85,
                      borderBottom: '1px solid', borderColor: 'divider',
                      cursor: oos ? 'not-allowed' : 'pointer',
                      opacity: oos ? 0.45 : 1,
                      bgcolor: idx === 0 && !oos ? alpha(P.blue, 0.05) : inCart ? alpha(P.green, 0.04) : 'transparent',
                      borderLeft: idx === 0 && !oos ? `3px solid ${P.blue}` : '3px solid transparent',
                      transition: 'background 0.08s',
                      '&:hover': oos ? {} : { bgcolor: alpha(P.blue, 0.07) },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontSize={12.5} fontWeight={inCart ? 700 : 500} noWrap lineHeight={1.3}>
                          <HighlightText text={p.title || ''} query={searchQuery} />
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.2 }}>
                          {catName && (
                            <Typography fontSize={9.5} color="text.disabled" sx={{ bgcolor: 'action.hover', borderRadius: 0.75, px: 0.5, lineHeight: 1.7 }}>
                              {catName}
                            </Typography>
                          )}
                          {oos && <Typography fontSize={9.5} color="error.main" fontWeight={700}>OUT OF STOCK</Typography>}
                          {!oos && stock <= 5 && <Typography fontSize={9.5} color="warning.main" fontWeight={700}>LOW: {stock}</Typography>}
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Typography fontSize={13} fontWeight={800} color={inCart ? 'success.main' : 'primary.main'}>
                          {KES(p.price)}
                        </Typography>
                        {inCart && (
                          <Typography fontSize={10} color="success.main" fontWeight={700}>×{inCart.quantity}</Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        {/* ── CENTER: Order Panel ── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'background.default', overflow: 'hidden', minWidth: 0 }}>

          {/* Customer row */}
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, bgcolor: 'background.paper' }}>
            {customer ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: alpha(P.green, 0.08), borderRadius: 2, px: 1.5, py: 0.7, border: `1px solid ${alpha(P.green, 0.3)}` }}>
                <CheckCircle sx={{ color: 'success.main', fontSize: 15 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography fontSize={12} fontWeight={700}>{customer.name}</Typography>
                  <Typography fontSize={10} color="text.disabled">{customer.loyaltyPoints || 0} loyalty pts</Typography>
                </Box>
                <IconButton size="small" onClick={() => { setCustomer(null); setCustomerPhone(''); }}>
                  <Close sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small" placeholder="Customer phone (optional)"
                  value={customerPhone}
                  onChange={e => setCustomerPhone(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleFindCustomer()}
                  InputProps={{ startAdornment: <InputAdornment position="start"><PersonSearch sx={{ fontSize: 15, color: 'text.disabled' }} /></InputAdornment> }}
                  sx={{ flex: 1 }}
                />
                <Button size="small" variant="outlined" onClick={handleFindCustomer} disabled={searchingCustomer} sx={{ minWidth: 56 }}>
                  {searchingCustomer ? <CircularProgress size={13} /> : 'Find'}
                </Button>
              </Box>
            )}
          </Box>

          {/* Cart items — scrollable */}
          <Box sx={{
            flex: 1, overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
          }}>
            {cart.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.3 }}>
                <ShoppingCart sx={{ fontSize: 52, color: 'text.disabled', mb: 1 }} />
                <Typography fontSize={13} color="text.secondary">Scan or tap products to add items</Typography>
              </Box>
            ) : (
              <Box>
                {cart.map((item, idx) => {
                  const isSel = selectedLine === item.product._id;
                  return (
                    <Box
                      key={item.product._id}
                      onClick={() => setSelectedLine(isSel ? null : item.product._id)}
                      sx={{
                        px: 2, py: isSel ? 1.2 : 0.8,
                        borderBottom: '1px solid', borderColor: 'divider',
                        bgcolor: isSel ? alpha(P.blue, 0.06) : idx % 2 === 0 ? 'background.default' : alpha(palette.mode === 'dark' ? '#fff' : '#000', 0.015),
                        cursor: 'pointer', transition: 'background 0.08s',
                        '&:hover': { bgcolor: alpha(P.blue, isSel ? 0.09 : 0.04) },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Line number */}
                        <Typography fontSize={10} color="text.disabled" sx={{ minWidth: 20, fontFamily: 'monospace', flexShrink: 0 }}>
                          {String(idx + 1).padStart(2, '0')}
                        </Typography>

                        {/* Name + unit price */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontSize={13} fontWeight={isSel ? 700 : 500} noWrap>
                            {item.product.title}
                          </Typography>
                          <Typography fontSize={11} color="text.disabled">
                            {KES(item.product.price)} each
                          </Typography>
                        </Box>

                        {/* Qty controls (expanded when selected) */}
                        {isSel ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={e => e.stopPropagation()}>
                            <IconButton size="small" onClick={() => updateQty(item.product._id, -1)}
                              sx={{ width: 26, height: 26, bgcolor: alpha(P.red, 0.1), color: 'error.main', '&:hover': { bgcolor: alpha(P.red, 0.2) } }}>
                              <Remove sx={{ fontSize: 13 }} />
                            </IconButton>
                            <Typography fontWeight={900} fontSize={15} sx={{ minWidth: 28, textAlign: 'center' }}>
                              {item.quantity}
                            </Typography>
                            <IconButton size="small" onClick={() => updateQty(item.product._id, 1)}
                              sx={{ width: 26, height: 26, bgcolor: alpha(P.green, 0.1), color: 'success.main', '&:hover': { bgcolor: alpha(P.green, 0.2) } }}>
                              <Add sx={{ fontSize: 13 }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => removeFromCart(item.product._id)}
                              sx={{ width: 26, height: 26, bgcolor: alpha(P.red, 0.1), color: 'error.main', ml: 0.5, '&:hover': { bgcolor: alpha(P.red, 0.2) } }}>
                              <Delete sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Box>
                        ) : (
                          <Typography fontSize={11} color="text.secondary" sx={{ minWidth: 40, textAlign: 'center' }}>
                            ×{item.quantity}
                          </Typography>
                        )}

                        {/* Line total */}
                        <Typography fontSize={13} fontWeight={700} color={isSel ? 'primary.main' : 'text.primary'} sx={{ minWidth: 90, textAlign: 'right', flexShrink: 0 }}>
                          {KES(item.product.price * item.quantity)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
                <div ref={cartEndRef} />
              </Box>
            )}
          </Box>

          {/* Discount row */}
          <Box sx={{ px: 2, py: 0.8, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0, display: 'flex', alignItems: 'center', minHeight: 40, bgcolor: 'background.paper' }}>
            {!showDiscount && !discount ? (
              <Button size="small" startIcon={<LocalOffer sx={{ fontSize: 12 }} />}
                onClick={() => setShowDiscount(true)}
                sx={{ fontSize: 11, textTransform: 'none', color: 'text.disabled', p: 0 }}>
                Add discount / coupon code
              </Button>
            ) : discount ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalOffer sx={{ fontSize: 13, color: 'success.main' }} />
                <Typography fontSize={12} color="success.main" fontWeight={700}>{discountCode}: −{KES(discountAmt)}</Typography>
                <IconButton size="small" onClick={() => { setDiscount(null); setDiscountCode(''); setShowDiscount(false); }}>
                  <Close sx={{ fontSize: 13 }} />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                <TextField
                  size="small" placeholder="DISCOUNT CODE"
                  value={discountCode}
                  onChange={e => setDiscountCode(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && handleApplyDiscount()}
                  inputProps={{ style: { textTransform: 'uppercase', fontSize: 12 } }}
                  sx={{ flex: 1 }}
                />
                <Button size="small" variant="outlined" onClick={handleApplyDiscount}
                  disabled={validatingDiscount || !discountCode.trim()} sx={{ minWidth: 60 }}>
                  {validatingDiscount ? <CircularProgress size={12} /> : 'Apply'}
                </Button>
                <IconButton size="small" onClick={() => { setShowDiscount(false); setDiscountCode(''); }}>
                  <Close sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            )}
            {discountError && <Typography color="error" fontSize={10} ml={1}>{discountError}</Typography>}
          </Box>

          {/* Totals */}
          <Box sx={{ px: 2, py: 1.2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
              <Typography fontSize={12} color="text.secondary">{cart.length} item{cart.length !== 1 ? 's' : ''}</Typography>
              <Typography fontSize={12} color="text.secondary">{KES(subtotal)}</Typography>
            </Box>
            {discountAmt > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography fontSize={12} color="success.main">Discount ({discountCode})</Typography>
                <Typography fontSize={12} color="success.main" fontWeight={700}>−{KES(discountAmt)}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
              <Typography fontSize={11} color="text.disabled">VAT 16% (incl.)</Typography>
              <Typography fontSize={11} color="text.disabled">{KES(vat)}</Typography>
            </Box>
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              py: 1, px: 1.5, borderRadius: 2,
              bgcolor: cart.length > 0 ? alpha(P.blue, 0.06) : 'action.hover',
              border: `1px solid ${cart.length > 0 ? alpha(P.blue, 0.2) : P.border}`,
            }}>
              <Typography fontWeight={700} fontSize={13} color="text.secondary" letterSpacing={1}>TOTAL</Typography>
              <Typography fontWeight={900} fontSize={28} color={cart.length > 0 ? 'primary.main' : 'text.disabled'} letterSpacing={-0.5}>
                {KES(total)}
              </Typography>
            </Box>
          </Box>

          {/* Payment method */}
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[
                { v: 'cash',  label: 'Cash',   icon: <AttachMoney sx={{ fontSize: 16 }} /> },
                { v: 'mpesa', label: 'M-Pesa', icon: <PhoneAndroid sx={{ fontSize: 16 }} /> },
                { v: 'card',  label: 'Card',   icon: <CreditCard sx={{ fontSize: 16 }} /> },
              ].map(m => (
                <Button key={m.v} startIcon={m.icon}
                  variant={paymentMethod === m.v ? 'contained' : 'outlined'}
                  onClick={() => { setPaymentMethod(m.v); refocusScanner(); }}
                  sx={{ flex: 1, fontWeight: 700, fontSize: 12, textTransform: 'none', borderRadius: 2, py: 0.8 }}
                >
                  {m.label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Cash received input */}
          {paymentMethod === 'cash' && (
            <Box sx={{ px: 2, pb: 1.5, flexShrink: 0 }}>
              <TextField
                fullWidth
                label="Cash Received"
                value={amountReceived}
                onChange={e => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setAmountReceived(v);
                }}
                placeholder="0.00"
                type="text"
                inputMode="decimal"
                InputProps={{
                  startAdornment: <InputAdornment position="start">
                    <Typography fontWeight={700} fontSize={14} color={cashReady ? 'success.main' : 'text.secondary'}>KES</Typography>
                  </InputAdornment>,
                  sx: {
                    borderRadius: 2,
                    '& input': { fontSize: 24, fontWeight: 900, fontFamily: 'monospace', py: 1, color: cashReady ? P.green : undefined },
                    '& fieldset': { borderColor: cashReady ? alpha(P.green, 0.6) : undefined, borderWidth: cashReady ? 2 : 1 },
                  },
                }}
              />
            </Box>
          )}

          {/* M-Pesa ref */}
          {paymentMethod === 'mpesa' && (
            <Box sx={{ px: 2, pb: 1.5, flexShrink: 0 }}>
              <TextField fullWidth size="small" label="M-Pesa Reference (optional)"
                value={mpesaRef} onChange={e => setMpesaRef(e.target.value)} placeholder="e.g. RHK12345XY" />
            </Box>
          )}

          {/* Card info */}
          {paymentMethod === 'card' && (
            <Box sx={{ px: 2, pb: 1.5, flexShrink: 0 }}>
              <Box sx={{ bgcolor: alpha(P.blue, 0.07), borderRadius: 2, p: 1.5, textAlign: 'center', border: `1px solid ${alpha(P.blue, 0.2)}` }}>
                <CreditCard sx={{ color: 'primary.main', fontSize: 24, mb: 0.3 }} />
                <Typography fontSize={13} color="primary.main" fontWeight={600}>Swipe / insert card — {KES(total)}</Typography>
              </Box>
            </Box>
          )}

          {/* Sale error */}
          {saleError && (
            <Alert severity="error" sx={{ mx: 2, mb: 1, py: 0.3, fontSize: 12, flexShrink: 0 }} onClose={() => setSaleError('')}>
              {saleError}
            </Alert>
          )}
        </Box>

        {/* ── RIGHT: Payment Numpad + Actions ── */}
        <Box sx={{
          width: 270, flexShrink: 0, display: 'flex', flexDirection: 'column',
          bgcolor: 'background.paper', borderLeft: '1px solid', borderColor: 'divider',
          overflow: 'hidden',
        }}>
          {/* Amount / change display */}
          <Box sx={{ px: 1.5, pt: 1.2, pb: 0.8, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            {cashReady ? (
              <Box sx={{ bgcolor: alpha(P.green, 0.1), border: `1px solid ${alpha(P.green, 0.35)}`, borderRadius: 2, p: 1, textAlign: 'center' }}>
                <Typography fontSize={9} color="success.main" fontWeight={700} letterSpacing={0.8}>CHANGE DUE</Typography>
                <Typography fontSize={22} color="success.main" fontWeight={900} fontFamily="monospace">{KES(change)}</Typography>
              </Box>
            ) : (
              <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                <Typography fontSize={9} color="text.disabled" fontWeight={600} letterSpacing={0.8}>
                  {paymentMethod === 'cash' ? 'BALANCE DUE' : 'AMOUNT'}
                </Typography>
                <Typography fontSize={22} color="text.primary" fontWeight={900} fontFamily="monospace">{KES(total)}</Typography>
              </Box>
            )}
          </Box>

          {/* Numpad (cash only) */}
          {paymentMethod === 'cash' && (
            <Box sx={{ px: 1, py: 0.8, flexShrink: 0 }}>
              {/* Quick amounts */}
              <Box sx={{ display: 'flex', gap: 0.5, mb: 0.8, flexWrap: 'wrap' }}>
                <Button size="small" onClick={() => handleNumpad('EXACT')}
                  sx={{ flex: 1, fontSize: 10, fontWeight: 700, bgcolor: alpha(P.green, 0.1), color: 'success.main', border: `1px solid ${alpha(P.green, 0.3)}`, borderRadius: 1.5, minWidth: 0, height: 26, textTransform: 'none' }}>
                  EXACT
                </Button>
                {[50, 100, 200, 500, 1000].map(a => (
                  <Button key={a} size="small" onClick={() => handleNumpad(a)}
                    sx={{ flex: 1, fontSize: 10, fontWeight: 600, bgcolor: 'action.hover', color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, minWidth: 0, height: 26 }}>
                    {a}
                  </Button>
                ))}
              </Box>

              {/* Digit grid */}
              <Grid container spacing={0.5}>
                {[7, 8, 9, 4, 5, 6, 1, 2, 3, '.', 0, '00'].map(k => (
                  <Grid item xs={4} key={String(k)}>
                    <Button fullWidth onClick={() => handleNumpad(String(k))}
                      sx={{
                        height: 36, fontSize: 16, fontWeight: 700, borderRadius: 1.5, minWidth: 0,
                        bgcolor: 'action.hover', color: 'text.primary',
                        border: '1px solid', borderColor: 'divider',
                        '&:hover': { bgcolor: 'action.selected', borderColor: 'primary.main' },
                      }}>
                      {k}
                    </Button>
                  </Grid>
                ))}
                <Grid item xs={8}>
                  <Button fullWidth onClick={() => handleNumpad('BACK')}
                    sx={{ height: 36, fontSize: 12, fontWeight: 700, borderRadius: 1.5, minWidth: 0, bgcolor: alpha(P.red, 0.08), color: 'error.main', border: `1px solid ${alpha(P.red, 0.2)}`, '&:hover': { bgcolor: alpha(P.red, 0.15) } }}>
                    ⌫ BACK
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button fullWidth onClick={() => handleNumpad('CLR')}
                    sx={{ height: 36, fontSize: 11, fontWeight: 700, borderRadius: 1.5, minWidth: 0, bgcolor: alpha(P.red, 0.08), color: 'error.main', border: `1px solid ${alpha(P.red, 0.2)}`, '&:hover': { bgcolor: alpha(P.red, 0.15) } }}>
                    CLR
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {paymentMethod === 'mpesa' && (
            <Box sx={{ px: 1.5, py: 2, flexShrink: 0, textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <PhoneAndroid sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography fontSize={12} color="text.secondary">Enter M-Pesa reference in the order panel</Typography>
            </Box>
          )}

          {paymentMethod === 'card' && (
            <Box sx={{ px: 1.5, py: 2, flexShrink: 0, textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <CreditCard sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography fontSize={12} color="text.secondary">Swipe or insert card on terminal</Typography>
            </Box>
          )}

          <Box sx={{ flex: 1 }} />

          {/* Action buttons */}
          <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.8, flexShrink: 0 }}>
            {/* Hold + Process Next */}
            <Box sx={{ display: 'flex', gap: 0.8 }}>
              <Button fullWidth variant="contained" color="warning"
                startIcon={<PauseCircle sx={{ fontSize: 15 }} />}
                onClick={holdTransaction}
                disabled={cart.length === 0}
                sx={{ height: 38, borderRadius: 2, fontSize: 11, fontWeight: 800, textTransform: 'none' }}>
                HOLD
              </Button>
              <Button fullWidth variant="contained" color="secondary"
                startIcon={<ArrowForward sx={{ fontSize: 15 }} />}
                onClick={processNext}
                disabled={heldTxns.length === 0}
                sx={{ height: 38, borderRadius: 2, fontSize: 11, fontWeight: 800, textTransform: 'none', position: 'relative' }}>
                NEXT
                {heldTxns.length > 0 && (
                  <Box sx={{
                    position: 'absolute', top: -7, right: -4,
                    bgcolor: 'warning.main', color: '#fff', borderRadius: '50%',
                    width: 17, height: 17, fontSize: 9, fontWeight: 900,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid', borderColor: 'background.paper',
                  }}>
                    {heldTxns.length}
                  </Box>
                )}
              </Button>
            </Box>

            {/* Process Sale */}
            <Button variant="contained" color="success" fullWidth
              onClick={handleProcessSale}
              disabled={processing || cart.length === 0}
              sx={{
                height: 52, borderRadius: 2.5, fontSize: 14, fontWeight: 900,
                textTransform: 'none', letterSpacing: 0.3,
                boxShadow: cart.length > 0 ? `0 4px 18px ${alpha(P.green, 0.35)}` : 'none',
              }}
            >
              {processing ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptLong sx={{ fontSize: 18 }} />
                  <Box>
                    <Typography fontSize={13} fontWeight={900} lineHeight={1.1}>PROCESS SALE</Typography>
                    {cart.length > 0 && <Typography fontSize={10} fontWeight={600} lineHeight={1} sx={{ opacity: 0.85 }}>{KES(total)}</Typography>}
                  </Box>
                </Box>
              )}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ══ SHIFT DIALOG ══ */}
      <Dialog open={shiftOpen} maxWidth="xs" fullWidth onClose={() => setShiftOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: 16, borderBottom: '1px solid', borderColor: 'divider', py: 1.5 }}>
          {currentShift ? 'Close Shift' : 'Open Shift'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {currentShift
              ? 'Count the cash in the drawer and enter the closing float.'
              : 'Enter the opening float to begin a new cashier shift.'}
          </Typography>
          <TextField fullWidth size="small"
            label={currentShift ? 'Closing float (KES)' : 'Opening float (KES)'}
            type="number"
            value={currentShift ? closeFloat : openFloat}
            onChange={e => currentShift ? setCloseFloat(e.target.value) : setOpenFloat(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setShiftOpen(false)}>Cancel</Button>
          {currentShift ? (
            <Button variant="contained" color="error" onClick={handleCloseShift} disabled={shiftProcessing}>
              {shiftProcessing ? <CircularProgress size={15} /> : 'Close Shift'}
            </Button>
          ) : (
            <Button variant="contained" color="success" onClick={handleOpenShift} disabled={shiftProcessing}>
              {shiftProcessing ? <CircularProgress size={15} /> : 'Open Shift'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ══ ACTIVITY FEED DRAWER ══ */}
      <Drawer
        anchor="right"
        open={showFeed}
        onClose={() => setShowFeed(false)}
        PaperProps={{ sx: { width: 340, display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography fontWeight={800} fontSize={15}>Live POS Activity</Typography>
            <Typography fontSize={11} color="text.secondary">All terminals · real-time</Typography>
          </Box>
          <IconButton size="small" onClick={() => setShowFeed(false)}><Close sx={{ fontSize: 16 }} /></IconButton>
        </Box>

        {/* Online terminals */}
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: alpha(P.green, 0.04) }}>
          <Typography fontSize={11} fontWeight={700} color="text.secondary" mb={0.5}>ONLINE TERMINALS</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Chip
              size="small"
              icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main', ml: '6px !important' }} />}
              label={`${terminalId.current} (this)`}
              sx={{ fontSize: 10, height: 22, bgcolor: alpha(P.green, 0.12), border: `1px solid ${alpha(P.green, 0.3)}` }}
            />
            {onlineTerminals.map(t => (
              <Chip
                key={t.terminalId}
                size="small"
                icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main', ml: '6px !important' }} />}
                label={t.cashierName ? `${t.terminalId} · ${t.cashierName}` : t.terminalId}
                sx={{ fontSize: 10, height: 22, bgcolor: alpha(P.green, 0.08) }}
              />
            ))}
            {onlineTerminals.length === 0 && (
              <Typography fontSize={10} color="text.disabled">No other terminals online</Typography>
            )}
          </Box>
        </Box>

        {/* Live terminal carts */}
        {Object.keys(terminalCarts).length > 0 && (
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ px: 2, py: 0.8, bgcolor: alpha(P.blue, 0.04) }}>
              <Typography fontSize={11} fontWeight={700} color="text.secondary">LIVE CARTS</Typography>
            </Box>
            {Object.values(terminalCarts).map(tc => (
              <Box key={tc.terminalId} sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                {/* Terminal header row */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                    <Box sx={{
                      width: 7, height: 7, borderRadius: '50%',
                      bgcolor: tc.itemCount > 0 ? 'success.main' : 'text.disabled',
                      animation: tc.itemCount > 0 ? 'pulse 1.5s infinite' : 'none',
                      '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                    }} />
                    <Typography fontSize={12} fontWeight={700}>{tc.terminalId}</Typography>
                    {tc.editingHoldId && (
                      <Chip size="small" label="editing hold" sx={{ fontSize: 9, height: 16, bgcolor: alpha(P.orange, 0.15), color: 'warning.main' }} />
                    )}
                  </Box>
                  <Typography fontSize={12} fontWeight={800} color={tc.itemCount > 0 ? 'primary.main' : 'text.disabled'}>
                    KES {Number(tc.total || 0).toLocaleString()}
                  </Typography>
                </Box>

                {/* Item list with Push buttons */}
                {tc.items?.length > 0 ? (
                  <Box sx={{ pl: 0.5, mb: 0.8 }}>
                    {tc.items.map((item, i) => (
                      <Box key={i} sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5, py: 0.3,
                        borderRadius: 1, px: 0.5,
                        '&:hover': { bgcolor: alpha(P.blue, 0.06) },
                      }}>
                        <Typography fontSize={10} color="text.secondary" noWrap sx={{ flex: 1 }}>{item.title}</Typography>
                        <Typography fontSize={10} color="text.disabled">×{item.quantity}</Typography>
                        <Tooltip title={`Add to my cart`} arrow>
                          <IconButton size="small" sx={{ p: 0.3, color: 'primary.main' }}
                            onClick={() => {
                              socketRef.current?.emit('pos:push_item', {
                                targetTerminalId: terminalId.current,
                                fromTerminalId: tc.terminalId,
                                item,
                              });
                            }}>
                            <Add sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                    {tc.items.length > 6 && (
                      <Typography fontSize={10} color="text.disabled" pl={0.5}>+{tc.items.length - 6} more…</Typography>
                    )}
                  </Box>
                ) : (
                  <Typography fontSize={10} color="text.disabled" pl={0.5} mb={0.6}>Cart is empty</Typography>
                )}

                {/* Take Over button */}
                {tc.items?.length > 0 && (
                  <Button
                    size="small" fullWidth variant="outlined" color="warning"
                    sx={{ fontSize: 10, height: 26, textTransform: 'none', borderRadius: 1.5 }}
                    onClick={() => {
                      const newCart = (tc.items || []).map(item => ({
                        product: { _id: item.productId, title: item.title, price: item.unitPrice },
                        quantity: item.quantity,
                      }));
                      setCart(prev => {
                        // Merge: if item exists add qty, else append
                        const merged = [...prev];
                        for (const ni of newCart) {
                          const ex = merged.find(i => i.product._id?.toString() === ni.product._id?.toString());
                          if (ex) ex.quantity += ni.quantity;
                          else merged.push(ni);
                        }
                        return merged;
                      });
                      socketRef.current?.emit('pos:cart_takeover', {
                        fromTerminalId: tc.terminalId,
                        toTerminalId: terminalId.current,
                        takenBy: terminalId.current,
                        cart: tc.items,
                      });
                      setShowFeed(false);
                    }}
                  >
                    Take Over cart ({tc.items.length} item{tc.items.length !== 1 ? 's' : ''})
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Feed list */}
        <Box sx={{ flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider' } }}>
          {activityFeed.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, opacity: 0.35 }}>
              <MonitorHeart sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography fontSize={12} color="text.secondary">Waiting for activity…</Typography>
            </Box>
          ) : (
            activityFeed.map((ev, i) => {
              const colors = { sale: P.green, void: P.red, refund: P.orange, hold: P.orange, shift_open: P.blue, shift_close: P.purple };
              const icons = {
                sale: <PointOfSale sx={{ fontSize: 15 }} />,
                void: <Block sx={{ fontSize: 15 }} />,
                refund: <Undo sx={{ fontSize: 15 }} />,
                hold: <PauseCircle sx={{ fontSize: 15 }} />,
                shift_open: <CheckCircle sx={{ fontSize: 15 }} />,
                shift_close: <Close sx={{ fontSize: 15 }} />,
              };
              const labels = {
                sale: `Sale ${ev.saleNumber || ''} · ${ev.paymentMethod?.toUpperCase() || ''} · KES ${Number(ev.total || 0).toLocaleString()}`,
                void: `Void ${ev.saleNumber || ''} — ${ev.reason || ''}`,
                refund: `Refund ${ev.saleNumber || ''} — ${ev.reason || ''}`,
                hold: `Held ${ev.saleNumber || ''} · ${ev.itemCount || 0} item(s)`,
                shift_open: `Shift opened`,
                shift_close: `Shift closed · KES ${Number(ev.summary?.totalSales || 0).toLocaleString()} sales`,
              };
              const c = colors[ev.type] || P.blue;
              return (
                <Box key={i} sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 1.2, alignItems: 'flex-start' }}>
                  <Box sx={{ mt: 0.3, color: c, flexShrink: 0 }}>{icons[ev.type]}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize={12} fontWeight={600} color={c} noWrap>{labels[ev.type] || ev.type}</Typography>
                    <Typography fontSize={10} color="text.secondary">
                      {ev.cashier?.name || 'Cashier'}
                      {ev.terminalId ? ` · ${ev.terminalId}` : ''}
                    </Typography>
                    <Typography fontSize={10} color="text.disabled">
                      {ev.at ? new Date(ev.at).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
        <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" fullWidth onClick={() => setActivityFeed([])} sx={{ fontSize: 11, color: 'text.disabled' }}>
            Clear feed
          </Button>
        </Box>
      </Drawer>

      {/* ══ RECEIPT DIALOG ══ */}
      <Dialog open={receiptOpen} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: 'success.main', color: '#fff', display: 'flex', alignItems: 'center', gap: 1, py: 1.5, fontWeight: 800, fontSize: 15 }}>
          <CheckCircle sx={{ fontSize: 18 }} /> Sale Complete
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          {receipt && (
            <Box id="pos-receipt" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
              <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                <Typography fontWeight={900} fontSize={15} letterSpacing={1}>MTAANI SUPERMARKET</Typography>
                <Typography fontSize={11} color="text.secondary">
                  {new Date().toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })}
                </Typography>
                <Typography fontSize={11} color="text.secondary">
                  Sale # {receipt.saleNumber || receipt._id?.slice(-8).toUpperCase() || 'N/A'}
                </Typography>
                {receipt.customer && <Typography fontSize={11}>Customer: {receipt.customer.name}</Typography>}
              </Box>
              <Divider sx={{ borderStyle: 'dashed', mb: 1 }} />
              {receipt.items?.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                  <Typography fontSize={11} sx={{ flex: 1 }} noWrap>{item.product?.title || item.title}</Typography>
                  <Typography fontSize={11} sx={{ ml: 1, whiteSpace: 'nowrap', color: 'text.disabled' }}>
                    {item.quantity} × {KES(item.product?.price || item.unitPrice)}
                  </Typography>
                  <Typography fontSize={11} fontWeight={700} sx={{ ml: 1, minWidth: 72, textAlign: 'right' }}>
                    {KES((item.product?.price || item.unitPrice) * item.quantity)}
                  </Typography>
                </Box>
              ))}
              <Divider sx={{ borderStyle: 'dashed', my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography fontSize={11} color="text.secondary">Subtotal</Typography>
                <Typography fontSize={11} color="text.secondary">{KES(receipt.subtotal)}</Typography>
              </Box>
              {receipt.discountAmt > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography fontSize={11} color="success.main">Discount</Typography>
                  <Typography fontSize={11} color="success.main">−{KES(receipt.discountAmt)}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography fontSize={10} color="text.disabled">VAT 16% (incl.)</Typography>
                <Typography fontSize={10} color="text.disabled">{KES(receipt.vat)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography fontWeight={900} fontSize={14}>TOTAL</Typography>
                <Typography fontWeight={900} fontSize={14}>{KES(receipt.total)}</Typography>
              </Box>
              <Divider sx={{ borderStyle: 'dashed', my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography fontSize={11} color="text.secondary">Payment</Typography>
                <Typography fontSize={11} fontWeight={700}>{(receipt.paymentMethod || '').toUpperCase()}</Typography>
              </Box>
              {receipt.paymentMethod === 'cash' && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography fontSize={11} color="text.secondary">Cash Received</Typography>
                    <Typography fontSize={11}>{KES(receipt.amountReceived)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography fontSize={12} fontWeight={800}>Change</Typography>
                    <Typography fontSize={12} fontWeight={800} color="success.main">{KES(receipt.change)}</Typography>
                  </Box>
                </>
              )}
              {receipt.loyaltyPointsEarned > 0 && (
                <Box sx={{ mt: 1, bgcolor: alpha(P.orange, 0.1), borderRadius: 1, p: 0.8, textAlign: 'center', border: `1px solid ${alpha(P.orange, 0.3)}` }}>
                  <Typography fontSize={11} color="warning.main" fontWeight={600}>
                    +{receipt.loyaltyPointsEarned} loyalty points earned
                  </Typography>
                </Box>
              )}
              <Divider sx={{ borderStyle: 'dashed', my: 1 }} />
              <Typography fontSize={10} textAlign="center" color="text.disabled">Thank you for shopping at Mtaani!</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button startIcon={<Print sx={{ fontSize: 14 }} />} variant="outlined" size="small" onClick={() => window.print()}>
            Print
          </Button>
          <Button variant="contained" onClick={() => resetSale(true)} sx={{ flex: 1, fontWeight: 700 }}>
            New Sale
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
